/**
 * @file This file contains the `Search` component which provides a styled search input field
 * for the user interface.
 */

import { TextField, InputAdornment, styled } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { useContext, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { FilterStatesContext } from '../contexts/filterStates';

const StyledTextField = styled(TextField)(({ theme }) => ({
    borderRadius: 50,
    background: theme.palette.common.white,
    '& .MuiInputBase-input': {
        cursor: 'pointer'
    },
    '& .MuiInputBase-root': {
        '&:hover': {
            //hover background colour
            backgroundColor: theme.palette.filters.selected
        },
        '&:focus-within': {
            //selected background colour
            background: theme.palette.filters.selected
        }
    },
    '& .MuiOutlinedInput-root': {
        borderRadius: 50,
        '&:hover .MuiOutlinedInput-notchedOutline': {
            //hover border colour
            borderColor: theme.palette.filters.selected
        },
        '&:focus-within .MuiOutlinedInput-notchedOutline': {
            //selected border colour
            borderColor: theme.palette.filters.selected
        }
    },
    //non-selected border colour
    '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.filters.unselected
    }
}));

const StyledCloseIcon = styled(CloseIcon)(({ theme }) => ({
    cursor: 'pointer',
    '&:hover': {
        color: theme.palette.common.black
    }
}));

/**
 * The SearchBar component renders a text input field using the MUI TextField component with custom styles.
 * The input field is decorated with a search icon start adornment and a close icon end adornment for clearing the input.
 * Any text entered into the field will update the global SearchState context.
 *
 * @component
 * @returns {React.ReactElement} The rendered SearchBar component
 */
export const SearchBar = () => {
    const theme = useTheme();

    const { searchState, setSearchState } = useContext(FilterStatesContext);

    const [isFocused, setIsFocused] = useState(false);

    return (
        <StyledTextField
            id='ebabSearchBar'
            data-testid='EbabSearchBar'
            data-analyticsid='search-bar'
            fullWidth
            value={searchState || ''}
            label='Search...'
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            onChange={(event) => {
                setSearchState(event.target.value);
            }}
            InputLabelProps={{
                sx: {
                    paddingLeft: 4,
                    color: theme.palette.disabled.darkest
                },
                shrink: isFocused || searchState?.length > 0
            }}
            InputProps={{
                startAdornment: (
                    <InputAdornment position='start'>
                        <SearchIcon />
                    </InputAdornment>
                ),
                endAdornment: searchState && (
                    <InputAdornment position='end'>
                        <StyledCloseIcon
                            fontSize='small'
                            onClick={() => setSearchState(null)}
                        />
                    </InputAdornment>
                )
            }}
        />
    );
};
